// @flow
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Button, Text } from '@asigna/kit';
import Pagination from '@mui/material/Pagination';
import { BATCH_TRANSFER_DISABLED } from 'components/Dialog/TransactionDialog/WidthdrawNftDialog/constants';
import { ImageWithPlaceholder } from 'components/ImageWithPlaceholder/ImageWithPlaceholder';
import {
  SkeletonNftAddress,
  SkeletonNftItem,
  SkeletonNftTitle,
} from 'components/Skeletons/SkeletonAsset';
import useAddress from 'hooks/use-address';
import useNetwork from 'hooks/use-network';
import useSafe from 'hooks/use-safe';
import BackIcon from 'images/back_icon.svg';
import { isArray } from 'lodash';
import { CollectionNFTsPageData } from 'types/NftTypes';
import { AddressBalance } from 'utils';
import { shortenAddress } from 'utils/helper';
import { getCollectionPaginatedNFTsData } from 'utils/nft-tools';

import { useNftImages } from '../../../../../../store';

import { useNftCollectionDelailsStyle } from './NftCollectionDelailsStyle';

type Props = {
  nftCollectionId: string;
  balance: Pick<AddressBalance, 'non_fungible_tokens'>;
  setSelectedNFTCollectionId: (id: string) => void;
  // TODO_R: remove setters below once you add onSendNFTFromCollectionClick and onSendNFTClick event props and those setter outside
  setSendNftDialogOpened: (status: boolean) => void;
  setSelectedCollectionId: (collection: string) => void;
  setSelectedNFTIds: Dispatch<SetStateAction<string[] | null>>;
};

const ITEMS_PER_PAGE = 20;

export const NftCollectionDetails = ({
  nftCollectionId,
  setSelectedNFTCollectionId,
  setSendNftDialogOpened,
  setSelectedCollectionId,
  setSelectedNFTIds,
  balance,
}: Props) => {
  const [page, setPage] = useState(1);
  const classes = useNftCollectionDelailsStyle();
  const { safe } = useSafe();
  const walletAccount = useAddress() || '';
  const [, stacksNetwork] = useNetwork();

  const [collectionNFTsPageData, setCollectionNFTsPageData] =
    useState<CollectionNFTsPageData | null>(null);
  const [loading, setLoading] = useState(false);
  const nftImagesStore = useNftImages();

  useEffect(
    function loadCollectionPaginatedNFTsData() {
      const getData = async () => {
        const currentPage = page - 1;
        const data = await getCollectionPaginatedNFTsData(
          nftCollectionId.toString(),
          ITEMS_PER_PAGE.toString(),
          (currentPage * ITEMS_PER_PAGE).toString(),
          walletAccount,
          safe.address,
          stacksNetwork,
          balance,
          nftImagesStore
        );
        if (!data || isArray(data)) return;
        setCollectionNFTsPageData(data);
      };
      setLoading(true);
      getData().finally(() => setLoading(false));
    },
    [page, nftCollectionId]
  );

  const onSendNFTClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    event.stopPropagation();
    setSelectedNFTIds([id]);
    setSelectedCollectionId(nftCollectionId);
    setSendNftDialogOpened(true);
  };

  const onSendNFTFromCollectionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setSendNftDialogOpened(true);
    setSelectedCollectionId(`${collectionNFTsPageData?.addr}::${collectionNFTsPageData?.title}`);
    if (BATCH_TRANSFER_DISABLED) {
      setSelectedNFTIds(null);

      return;
    }
    setSelectedNFTIds(collectionNFTsPageData?.results.map((imageId) => imageId.id) || null);
  };

  return (
    <div className="px-0">
      <div className="flex flex-col sm:flex-row items-start justify-between mb-32 gap-8 sm:gap-4">
        <div className="flex gap-16">
          <Button
            onClick={() => setSelectedNFTCollectionId('')}
            leftIcon={BackIcon}
            colorType="inherit"
            size="sm"
            className="border border-[.5px] border-black-4 w-40 h-40"
          />
          <div className="flex flex-col gap-4">
            {loading ? (
              <SkeletonNftTitle />
            ) : (
              <Text message={collectionNFTsPageData?.title || ''} size="r20" color="white" />
            )}
            {loading ? (
              <SkeletonNftAddress />
            ) : (
              <Text
                message={shortenAddress(collectionNFTsPageData?.addr || '')}
                size="r14"
                color="regular"
              />
            )}
          </div>
        </div>
        {/* TODO_R: create SendNFTButton in NFTsTab/components and reuse it on NFTsTab page */}
        <Button
          disabled={!safe.owners.find((owner) => owner.address === walletAccount) || loading}
          colorType="primary"
          onClick={onSendNFTFromCollectionClick}
          message="Send NFT"
          className="min-w-[144px]"
        />
      </div>
      <div className="grid gap-24 sm:gap-32 grid-cols-2 md:grid-cols-4 lg::grid-cols-5">
        {loading && <SkeletonNftItem />}
        {!loading &&
          collectionNFTsPageData?.results.map((nftImage) => (
            <div className="flex flex-col items-center gap-12" key={nftImage.id}>
              <ImageWithPlaceholder
                src={nftImage.image}
                alt=""
                width="100%"
                height="auto"
                className="rounded-[12px]"
              />
              <Text
                message={nftImage.title || `#${nftImage.id}`}
                size="m14"
                color="white"
                className="break-words"
              />

              {safe.owners.find((owner) => owner.address === walletAccount) && (
                <Button
                  // className={classes.btnNftWithdraw}
                  onClick={(e) => onSendNFTClick(e, nftImage.id)}
                  message="Send NFT"
                  colorType="secondary"
                />
              )}
            </div>
          ))}
      </div>
      {collectionNFTsPageData && collectionNFTsPageData.total / ITEMS_PER_PAGE > 1 && (
        <Pagination
          classes={{ root: classes.navRoot }}
          defaultPage={1}
          siblingCount={0}
          boundaryCount={1}
          count={Math.ceil(collectionNFTsPageData.total / ITEMS_PER_PAGE)}
          page={page}
          onChange={(e, value) => setPage(value)}
        />
      )}
    </div>
  );
};
